<template>
  <div class="thankyou-step">

    <!-- Icon — changes with decision -->
    <div class="ty-icon" :class="iconClass">
      <!-- Approved / default (checkmark) -->
      <svg v-if="!isDeclined" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <!-- Declined (X) -->
      <svg v-else viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <!-- Heading -->
    <h2 class="ty-title">{{ heading }}</h2>
    <p class="ty-subtitle">{{ subtitle }}</p>

    <!-- Body copy -->
    <div class="ty-body">
      <p v-if="isPending">We're working quickly to review your application. Please allow <strong>1–2 business days</strong> for processing — we'll email you with an update as soon as we have one.</p>
      <p v-if="isPending">Keep an eye on your inbox for any emails from Flash Money. In the meantime, you can check your application status by logging in.</p>
      <p v-if="isApproved">Great news! Your application has been approved. Log in to your Flash Money account to review your loan agreement and next steps.</p>
      <p v-if="isDeclined">Unfortunately we're unable to proceed with your application at this time. If you have any questions, please contact our team — we're happy to help.</p>
    </div>

    <!-- Status steps -->
    <div class="ty-steps">

      <!-- Step 1: always done -->
      <div class="ty-step ty-step--done">
        <div class="ty-step__dot">
          <svg viewBox="0 0 16 16" fill="currentColor"><path d="M13.485 1.431a1.473 1.473 0 0 1 2.104 2.062l-7.84 9.801a1.473 1.473 0 0 1-2.12.04L.431 8.138a1.473 1.473 0 0 1 2.084-2.083l4.111 4.112 6.86-8.736z"/></svg>
        </div>
        <div class="ty-step__text">
          <span class="ty-step__label">Application submitted</span>
          <span class="ty-step__note">Done — we've received your details</span>
        </div>
      </div>

      <!-- Step 2: under review — done once a decision exists -->
      <div class="ty-step" :class="isPending ? 'ty-step--active' : 'ty-step--done'">
        <div class="ty-step__dot" :class="isPending ? 'ty-step__dot--pulse' : ''">
          <svg v-if="!isPending" viewBox="0 0 16 16" fill="currentColor"><path d="M13.485 1.431a1.473 1.473 0 0 1 2.104 2.062l-7.84 9.801a1.473 1.473 0 0 1-2.12.04L.431 8.138a1.473 1.473 0 0 1 2.084-2.083l4.111 4.112 6.86-8.736z"/></svg>
        </div>
        <div class="ty-step__text">
          <span class="ty-step__label">Under review</span>
          <span class="ty-step__note">{{ isPending ? 'Our team is assessing your application' : 'Assessment complete' }}</span>
        </div>
      </div>

      <!-- Step 3: decision -->
      <div class="ty-step" :class="decisionStepClass">
        <div class="ty-step__dot" :class="decisionDotClass">
          <svg v-if="isApproved" viewBox="0 0 16 16" fill="currentColor"><path d="M13.485 1.431a1.473 1.473 0 0 1 2.104 2.062l-7.84 9.801a1.473 1.473 0 0 1-2.12.04L.431 8.138a1.473 1.473 0 0 1 2.084-2.083l4.111 4.112 6.86-8.736z"/></svg>
          <svg v-else-if="isDeclined" viewBox="0 0 16 16" fill="currentColor"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/></svg>
        </div>
        <div class="ty-step__text">
          <span class="ty-step__label">{{ decisionLabel }}</span>
          <span class="ty-step__note">{{ decisionNote }}</span>
        </div>
      </div>

    </div>

    <!-- CTA -->
    <a href="/login" class="btn-fm btn-fm-primary ty-cta">
      <svg viewBox="0 0 16 16" fill="currentColor" style="width:14px;height:14px;margin-right:6px;" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0v-2z"/>
        <path d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"/>
      </svg>
      Log in to Flash Money
    </a>

    <slot name="controls" v-bind="{ prev: null, next: null }"/>
  </div>
</template>

<script>
import store from '@modules/guest/new-applicant/store/store';
import {NEW_APPLICATION} from '@store/constants';

// Statuses that count as an approved outcome
const APPROVED_STATUSES = [
  'Approved',
  'Approved and Waiting for Response',
  'Approved and Accepted',
  'Money Transferred',
  'Settled',
  'Paid Off',
];

// Statuses that count as a declined outcome
const DECLINED_STATUSES = [
  'Declined',
  'Auto Declined',
  'Auto Declined (Blacklisted)',
  'Auto Declined (Debt Management)',
  'Auto Declined (AFSA Debt Agreement)',
  'Auto Declined (Sent to Debt Collector)',
  'Auto Declined (No. of SACC Loans)',
  'Approved but Rejected',
];

const POLL_INTERVAL_MS = 30_000; // poll every 30 s while pending

export default {
  name: 'ThankYou',

  data() {
    return { pollTimer: null };
  },

  computed: {
    loanStatus() {
      return store.state[NEW_APPLICATION.MODULE_NAME].meta.loanStatus ?? null;
    },
    loanNumber() {
      return store.state[NEW_APPLICATION.MODULE_NAME].meta.loanNumber ?? null;
    },

    isApproved() {
      return this.loanStatus && APPROVED_STATUSES.some(
        s => s.toLowerCase() === this.loanStatus.toLowerCase()
      );
    },

    isDeclined() {
      return this.loanStatus && DECLINED_STATUSES.some(
        s => s.toLowerCase() === this.loanStatus.toLowerCase()
      );
    },

    isPending() {
      return !this.isApproved && !this.isDeclined;
    },

    // ── Icon ──────────────────────────────────────────────────────────────────
    iconClass() {
      if (this.isApproved) return 'ty-icon--approved';
      if (this.isDeclined) return 'ty-icon--declined';
      return '';
    },

    // ── Heading / subtitle ───────────────────────────────────────────────────
    heading() {
      if (this.isApproved) return 'Application Approved!';
      if (this.isDeclined) return 'Application Unsuccessful';
      return 'Application Submitted!';
    },

    subtitle() {
      if (this.isApproved) return 'Congratulations — your Flash Money loan has been approved.';
      if (this.isDeclined) return 'We were unable to approve your application at this time.';
      return 'Thank you for applying with Flash Money.';
    },

    // ── Step 3 ───────────────────────────────────────────────────────────────
    decisionStepClass() {
      if (this.isApproved) return 'ty-step--done';
      if (this.isDeclined) return 'ty-step--declined';
      return '';
    },

    decisionDotClass() {
      if (this.isDeclined) return 'ty-step__dot--declined';
      if (this.isPending)  return 'ty-step__dot--pending';
      return '';
    },

    decisionLabel() {
      if (this.isApproved) return 'Approved';
      if (this.isDeclined) return 'Unsuccessful';
      return 'Decision';
    },

    decisionNote() {
      if (this.isApproved) return 'Log in to accept your loan offer';
      if (this.isDeclined) return 'Contact us if you have any questions';
      return 'You\'ll receive an email with our answer';
    },
  },

  mounted() {
    // ThankYou is rendered with v-show (always in the DOM), so mounted() fires
    // for every user on every page load — even those still filling in step 1.
    // Only start polling once a loan actually exists in the store.
    this._startPolling();
  },

  beforeDestroy() {
    this._stopPolling();
  },

  watch: {
    // If the user submits during this session loanNumber transitions null → value.
    // Kick off polling at that point so the status page stays live.
    loanNumber(val) {
      if (val && this.isPending && !this.pollTimer) {
        this._startPolling();
      }
    },
  },

  methods: {
    _startPolling() {
      // Guard: don't poll until a loan has been submitted (loanNumber set).
      // Without this check, FETCH_DRAFT would fire every 30 s and reset
      // steps.current to 0 for users who are still filling in the form.
      if (!this.isPending || !this.loanNumber || this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        if (!this.isPending) { this._stopPolling(); return; }
        // syncStep: false — do NOT let the poll overwrite steps.current.
        store.dispatch(NEW_APPLICATION.ACTIONS.FETCH_DRAFT, { syncStep: false });
      }, POLL_INTERVAL_MS);
    },

    _stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
};
</script>

<!--
  All CSS for .thankyou-step / .ty-* classes now lives globally in
  shared/styles/_fm-design-system.scss, imported once by the root shell
  (LoanApplication.vue). Previously duplicated here scoped, byte-identical
  to bought-lead's copy — removed to avoid the two drifting apart again.
-->
